import {QueryFunctionContext, useQuery} from 'react-query';
import api from 'api';
import {TrackingDetails, TrackingDeliveryStatus} from 'types';

const BANNER_ONLY_STATUSES: TrackingDeliveryStatus[] = [
  'new',
  'in_progress',
  'ready_for_dispatch',
  'redispatch',
  'archived',
  'completed',
];

const getTrackingDetails = async ({queryKey}: QueryFunctionContext) => {
  const [, trackingId] = queryKey;
  const {data} = await api.get<TrackingDetails>(
    `/requests/track/${trackingId}/`
  );
  return data;
};

export const getStatusLabel = (status: TrackingDeliveryStatus): string => {
  const labels: Record<TrackingDeliveryStatus, string> = {
    new: 'Order Received',
    in_progress: 'Processing',
    ready_for_dispatch: 'Ready for Dispatch',
    delivering: 'Delivering',
    redispatch: 'Redelivery Scheduled',
    canceled: 'Canceled',
    completed: 'Delivered',
    archived: 'Archived',
  };
  return labels[status];
};

export const useTracking = ({trackingId}: {trackingId: string}) => {
  const {data: trackingDetails, isLoading} = useQuery(
    ['tracking', trackingId],
    getTrackingDetails,
    {
      enabled: !!trackingId,
      refetchOnWindowFocus: true,
    }
  );

  const status = trackingDetails?.status;

  const shouldShowBannerOnly = status
    ? BANNER_ONLY_STATUSES.includes(status)
    : false;

  const shouldShowMap = status === 'delivering';

  const shouldShowFullUI =
    status === 'canceled' || status === 'delivering' || status === 'completed';

  const driverName = trackingDetails?.route?.driver?.full_name ?? '';

  const eta = trackingDetails?.delivery?.arrival_time ?? '';

  const stopsBeforeCount =
    trackingDetails?.delivery?.delivery_stops_before ?? 0;

  const deliveryAddress = trackingDetails?.delivery_address;

  const formattedAddress = deliveryAddress
    ? `${deliveryAddress.address_line_1}${
        deliveryAddress.unit_address ? `, ${deliveryAddress.unit_address}` : ''
      }, ${deliveryAddress.city}, ${deliveryAddress.state} ${
        deliveryAddress.zipcode
      }`
    : '';

  return {
    trackingDetails,
    isLoading,
    status,
    shouldShowBannerOnly,
    shouldShowMap,
    shouldShowFullUI,
    driverName,
    eta,
    stopsBeforeCount,
    deliveryAddress,
    formattedAddress,
  };
};
